import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import Seo from "../components/site/Seo";
import { Container } from "../components/site/Ui";
import { PACKAGES, TRIAL } from "../data/site";
import { useAuth } from "../lib/AuthContext";
import { errMessage } from "../lib/api";
import GoogleSignInButton from "../components/GoogleSignInButton";
import PrivacyNote from "../components/site/PrivacyNote";
import { useLang } from "../lib/LangContext";


const field =
  "w-full rounded-xl border border-slate-300 px-4 py-3 text-base text-slate-900 outline-none transition-colors focus:border-[#F95738] focus:ring-2 focus:ring-[#F95738]/20";

export default function GetStarted() {
  const { tr } = useLang();
  const [params] = useSearchParams();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(
    PACKAGES.some((p) => p.id === params.get("package")) ? params.get("package") : "trial"
  );
  const [form, setForm] = useState({
    first_name: "", last_name: "", business_name: "", email: "", phone: "",
    password: "", confirm: "", agree: false, marketing: false,
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const n = {};
    if (!form.first_name.trim()) n.first_name = tr("Please enter your first name.");
    if (!form.last_name.trim()) n.last_name = tr("Please enter your last name.");
    if (!form.business_name.trim()) n.business_name = tr("Please enter your business name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) n.email = tr("Please enter a valid email address.");
    if (form.password.length < 8) n.password = tr("Please use at least 8 characters.");
    if (form.password !== form.confirm) n.confirm = tr("Passwords do not match.");
    if (!form.agree) n.agree = tr("Please accept the Terms & Conditions and Privacy Policy.");
    setErrors(n);
    if (Object.keys(n).length) return;

    setBusy(true);
    try {
      await register({
        first_name: form.first_name, last_name: form.last_name, business_name: form.business_name,
        email: form.email, phone: form.phone, password: form.password, package: pkg,
        marketing_opt_in: form.marketing,
      });
      toast.success("Account created. Let's build your Brand Profile.");
      navigate("/dashboard/brand-profile", { replace: true });
    } catch (err) {
      toast.error(errMessage(err));
      setErrors({ form: errMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo
        title="Get started — create your free PostMint account"
        description="Create your PostMint account and put your social media on autopilot. Start free, or pick Starter €19, Grow €39 or Pro €79 per month. No long-term contract."
        path="/get-started"
      />

      <Container className="py-14 lg:py-20">
        <div className="max-w-2xl">
          <h1 className="text-4xl font-extrabold text-slate-900 sm:text-5xl">{tr("Start your 7-day free trial.")}</h1>
          <p className="mt-5 text-base leading-relaxed text-slate-600 md:text-lg">
            {tr("Create your account and see PostMint working on your own business. You add a card so your plan continues without interruption — €0 today, cancel any time during the trial.")}
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-[1fr_1fr]">
          <section data-testid="get-started-step-1">
            <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-400">{tr("Step 1")}</h2>
            <p className="mt-1 font-display text-2xl font-bold text-slate-900">{tr("Choose your plan")}</p>
            <div className="mt-6 space-y-3">
              <button
                type="button"
                data-testid="package-option-trial"
                onClick={() => setPkg("trial")}
                aria-pressed={pkg === "trial"}
                className={`w-full rounded-2xl border p-5 text-left transition-all ${
                  pkg === "trial"
                    ? "border-[#F95738] bg-[#FFF7F4] ring-2 ring-[#F95738]/20"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-display text-lg font-bold text-slate-900">
                      {tr(TRIAL.name)} <span className="text-slate-400">{tr("7 days free, card required")}</span>
                    </p>
                    <p className="mt-1 text-sm text-slate-600">{tr(TRIAL.tagline)}</p>
                  </div>
                  {pkg === "trial" && (
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F95738] text-white">
                      <Check className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}
                </div>
              </button>

              {PACKAGES.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`package-option-${p.id}`}
                  onClick={() => setPkg(p.id)}
                  aria-pressed={pkg === p.id}
                  className={`w-full rounded-2xl border p-5 text-left transition-all ${
                    pkg === p.id
                      ? "border-[#F95738] bg-[#FFF7F4] ring-2 ring-[#F95738]/20"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-display text-lg font-bold text-slate-900">
                        {p.name} <span className="text-slate-400">{`€${p.price}/mo`}</span>
                      </p>
                      <p className="mt-1 text-sm text-slate-600">{tr(p.tagline)}</p>
                    </div>
                    {pkg === p.id && (
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F95738] text-white">
                        <Check className="h-4 w-4" aria-hidden="true" />
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate-500">
              {tr("Your 7-day trial costs €0 today. When it ends, your Grow plan starts automatically at €39 per month excl. VAT unless you cancel first — we email you before the first charge. Paid plans are billed in advance and renew automatically until you cancel in Dashboard → Subscription.")}{" "}
              <Link to="/legal/refund-policy" className="font-semibold text-[#F95738] hover:underline">
                {tr("Cancellation & refunds")}
              </Link>
            </p>
          </section>

          <section data-testid="get-started-step-2">
            <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-400">{tr("Step 2")}</h2>
            <p className="mt-1 font-display text-2xl font-bold text-slate-900">{tr("Create account")}</p>

            <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 soft-shadow sm:p-7">
              <GoogleSignInButton label={tr("Sign up with Google")} testId="signup-google-button" />
              <p className="mt-3 text-center text-xs text-slate-500">
                {tr("Signing up with Google starts your 7-day free trial. You add your card in the dashboard to activate it, and can cancel any time before it ends.")}
              </p>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <span className="h-px flex-1 bg-slate-200" aria-hidden="true" />
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{tr("or use email")}</span>
              <span className="h-px flex-1 bg-slate-200" aria-hidden="true" />
            </div>

            <form
              onSubmit={submit}
              noValidate
              data-testid="signup-form"
              className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 soft-shadow sm:p-7"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                {[
                  ["first_name", "First Name *", "text"],
                  ["last_name", "Last Name *", "text"],
                ].map(([key, label, type]) => (
                  <div key={key}>
                    <label htmlFor={`su-${key}`} className="text-sm font-semibold text-slate-800">{tr(label)}</label>
                    <input id={`su-${key}`} type={type} data-testid={`signup-${key.replace("_", "-")}-input`} className={`mt-2 ${field}`} value={form[key]} onChange={set(key)} />
                    {errors[key] && <p data-testid={`signup-${key.replace("_", "-")}-error`} className="mt-1.5 text-sm text-red-600">{errors[key]}</p>}
                  </div>
                ))}
              </div>

              {[
                ["business_name", "Business Name *", "text"],
                ["email", "Email Address *", "email"],
                ["phone", "Phone Number", "tel"],
                ["password", "Password *", "password"],
                ["confirm", "Confirm Password *", "password"],
              ].map(([key, label, type]) => (
                <div key={key} className="mt-5">
                  <label htmlFor={`su-${key}`} className="text-sm font-semibold text-slate-800">{tr(label)}</label>
                  <input
                    id={`su-${key}`}
                    type={type}
                    autoComplete={type === "password" ? "new-password" : "on"}
                    data-testid={`signup-${key.replace("_", "-")}-input`}
                    className={`mt-2 ${field}`}
                    value={form[key]}
                    onChange={set(key)}
                  />
                  {errors[key] && <p data-testid={`signup-${key.replace("_", "-")}-error`} className="mt-1.5 text-sm text-red-600">{errors[key]}</p>}
                </div>
              ))}

              <label htmlFor="su-agree" className="mt-6 flex items-start gap-3 text-sm text-slate-600">
                <input
                  id="su-agree"
                  type="checkbox"
                  data-testid="signup-terms-checkbox"
                  className="mt-1 h-4 w-4 accent-[#F95738]"
                  checked={form.agree}
                  onChange={set("agree")}
                />
                <span>
                  I agree to the{" "}
                  <Link to="/legal/terms" className="font-semibold text-[#F95738] hover:underline">Terms of Service</Link>{" "}
                  and acknowledge the{" "}
                  <Link to="/legal/privacy-policy" className="font-semibold text-[#F95738] hover:underline">Privacy Policy</Link>.
                </span>
              </label>
              {errors.agree && <p data-testid="signup-terms-error" className="mt-1.5 text-sm text-red-600">{errors.agree}</p>}

              <label htmlFor="su-marketing" className="mt-4 flex items-start gap-3 text-sm text-slate-600">
                <input
                  id="su-marketing"
                  type="checkbox"
                  data-testid="signup-marketing-checkbox"
                  className="mt-1 h-4 w-4 accent-[#F95738]"
                  checked={form.marketing}
                  onChange={set("marketing")}
                />
                <span>
                  {tr("Optional: send me occasional PostMint tips and offers by email. This is separate from account emails and you can unsubscribe at any time.")}
                </span>
              </label>

              <PrivacyNote
                className="mt-4"
                purpose="We use these details to create and run your account, and to send you account and billing email."
                testId="signup-privacy-note"
              />

              {errors.form && (
                <p data-testid="signup-form-error" className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                  {errors.form}
                </p>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="signup-submit-button"
                className="mt-6 w-full rounded-full bg-[#F95738] px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#E0482B] disabled:opacity-60"
              >
                {busy ? tr("Creating account…") : tr("Continue")}
              </button>

              <p className="mt-4 text-center text-sm text-slate-600">
                {tr("Already have an account?")}{" "}
                <Link to="/login" data-testid="signup-login-link" className="font-semibold text-[#F95738] hover:underline">
                  {tr("Login")}
                </Link>
              </p>
            </form>
          </section>
        </div>
      </Container>
    </>
  );
}
